import { AppointmentModel } from "app/services/api/types/calendar/models/AppointmentModel";
import { useForm, useWatch } from "react-hook-form";
import { LocationTypeEnum } from "app/services/api/types/calendar/enums/LocationTypeEnum";
import { CreateCalendarEventForm } from "app/services/api/types/calendar/requests/CreateCalendarEventForm";
import { format } from "date-fns";
import { FrequencyEnums } from "app/services/api/types/calendar/enums/FrequencyEnums";
import { CreateCalendarEventRequest } from "app/services/api/types/calendar/requests/CreateCalendarEventRequest";
import { AppointmentStatusEnums } from "app/services/api/types/calendar/enums/AppointmentStatusEnums";
import { formatDateTime } from "ui/utils/dateUtils";
import { useRecurrenceRuleBuilder } from "./useRecurrenceRuleBuilder";
import {
  useCreateCalendarEventMutation,
  useUpdateCalendarEventMutation,
} from "app/services/api/calendarApi";
import { useDisplayToast } from "ui/hooks/useDisplayToast";
import { KeyValueModel } from "app/services/api/types/common/models/KeyValueModel";
import { useHandleAllResidents } from "./useHandleAllResidents";
import { RecurrenceModel } from "app/services/api/types/calendar/models/RecurrenceModel";
import { RecurrenceFormModel } from "app/services/api/types/calendar/models/RecurrenceFormModel";
import { useEffect, useRef } from "react";

export interface InitialTimeInterface {
  startDate?: string;
  startTime?: string;
  endDate?: string;
  endTime?: string;
  locationId?: number;
  residentId?: number;
}
interface UseHandleAppointmentsProps {
  initialAppointment?: AppointmentModel | null;
  initialTimes?: InitialTimeInterface;
  onCreateSuccess?: () => void;
}

export function useHandleAppointments({
  initialAppointment,
  initialTimes,
  onCreateSuccess,
}: UseHandleAppointmentsProps) {
  const isFirstRender = useRef(true);

  const { buildRecurrenceRule } = useRecurrenceRuleBuilder();
  const { showNewToast, showErrorToast } = useDisplayToast();
  const [createCalendarEvent] = useCreateCalendarEventMutation();
  const [updateCalendarEvent] = useUpdateCalendarEventMutation();

  const parseAdditionalInfo = (additionalInfo?: KeyValueModel[]): string[] => {
    if (!additionalInfo?.length) return [];
    const transportRequired =
      additionalInfo.find((info) => info.key === "transport_required")
        ?.value === "true";
    const transportBooked =
      additionalInfo.find((info) => info.key === "transport_booked")?.value ===
      "true";

    if (transportRequired) {
      return transportBooked ? ["booked"] : ["not_booked"];
    } else {
      return ["no_transport"];
    }
  };

  const parseRecurrenceRule = (
    recurrenceRule?: RecurrenceModel | null,
  ): RecurrenceFormModel | null => {
    if (!recurrenceRule) return null;

    return {
      frequency: recurrenceRule.frequency ? [recurrenceRule.frequency] : [],
      end_date: recurrenceRule.end_date
        ? format(recurrenceRule.end_date, "yyyy-MM-dd")
        : null,
      max_occurrences: recurrenceRule.max_occurrences ?? null,
      daily: {
        option:
          recurrenceRule.interval === 1 &&
          recurrenceRule.week_day &&
          [...recurrenceRule.week_day].sort().join("") === "12345"
            ? "weekday"
            : "interval",
        interval: recurrenceRule.interval,
        week_day: recurrenceRule.week_day ?? [],
      },
      weekly: {
        interval: recurrenceRule.interval,
        week_day: recurrenceRule.week_day ?? [],
      },
      monthly: {
        option: recurrenceRule.by_set_position
          ? "weekday_position"
          : "specific_day",
        interval: recurrenceRule.interval,
        week_day: recurrenceRule.week_day?.length
          ? `${recurrenceRule.week_day[0]}`
          : "",
        by_set_position: recurrenceRule.by_set_position,
      },
      yearly: {
        option: recurrenceRule.by_set_position
          ? "day_position_of_the_month"
          : "specific_day_of_the_month",
        interval: recurrenceRule.interval,
        week_day: recurrenceRule.week_day?.length
          ? `${recurrenceRule.week_day[0]}`
          : "",
        by_month: recurrenceRule.by_month,
        by_set_position: recurrenceRule.by_set_position,
      },
    };
  };

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    formState: { isSubmitting },
  } = useForm<CreateCalendarEventForm>({
    defaultValues: {
      title: initialAppointment?.title || "",
      start_date: initialAppointment?.starts_at
        ? format(new Date(initialAppointment.starts_at), "yyyy-MM-dd")
        : initialTimes?.startDate || "",
      start_time: initialAppointment?.starts_at
        ? format(new Date(initialAppointment.starts_at), "HH:mm")
        : initialTimes?.startTime || "",
      end_date: initialAppointment?.ends_at
        ? format(new Date(initialAppointment.ends_at), "yyyy-MM-dd")
        : initialTimes?.endDate || "",
      end_time: initialAppointment?.ends_at
        ? format(new Date(initialAppointment.ends_at), "HH:mm")
        : initialTimes?.endTime || "",
      participant_ids:
        initialAppointment?.participants.map((participant) => participant.id) ||
        (initialTimes?.residentId ? [initialTimes?.residentId] : []),
      type: initialAppointment?.type || undefined,
      type_specification: initialAppointment?.type_specification || "",
      location_id:
        initialAppointment?.location?.id ||
        initialTimes?.locationId ||
        undefined,
      location_type:
        (initialAppointment?.location_type as LocationTypeEnum) || undefined,
      location_description: initialAppointment?.location_description || "",
      status: initialAppointment?.status || AppointmentStatusEnums.SCHEDULED,
      notes: initialAppointment?.notes || undefined,
      role_ids: initialAppointment?.roles.map((role) => role.id) || [],
      all_residents: initialAppointment?.all_residents || false,
      additional_info: parseAdditionalInfo(initialAppointment?.additional_info),
      recurrence_rule: parseRecurrenceRule(
        initialAppointment?.recurrence_rule,
      ) || {
        frequency: [FrequencyEnums.NEVER],
        daily: {
          interval: 1,
        },
        weekly: {
          interval: 1,
        },
        monthly: {
          interval: 1,
        },
        yearly: {
          interval: 1,
        },
      },
      endsOption: initialAppointment?.recurrence_rule?.end_date
        ? "date"
        : initialAppointment?.recurrence_rule?.max_occurrences
          ? "occurrences"
          : "never",

      preparation_needed: initialAppointment?.preparation_needed || false,
    },
  });

  const startDate = watch("start_date");
  const startTime = watch("start_time");

  useEffect(() => {
    if (startDate && startTime) {
      const [hours = "0", minutes = "0"] = startTime?.split(":") ?? [];
      const start = new Date(startDate);
      start.setHours(+hours, +minutes);

      const end = new Date(start.getTime() + 30 * 60 * 1000);

      setValue("end_date", end.toISOString().slice(0, 10));

      const hh = end.getHours().toString().padStart(2, "0");
      const mm = end.getMinutes().toString().padStart(2, "0");
      setValue("end_time", `${hh}:${mm}`);
    }
  }, [startDate, startTime, setValue]);

  const locationSelected = useWatch({
    control,
    name: "location_id",
  });

  // Clear selected residents when location changes
  useEffect(() => {
    // Skip on initial render to avoid clearing pre-filled values
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    // When editing an existing appointment, don't clear residents
    if (initialAppointment) return;

    // Clear participant_ids and all_residents when location changes
    setValue("participant_ids", []);
    setValue("all_residents", false);
  }, [locationSelected, setValue, initialAppointment]);

  const { userOptions, areResidentsLoading, searchResidents } =
    useHandleAllResidents({ selectedLocationId: locationSelected });

  const allResidentsSelected = useWatch({
    control,
    name: "all_residents",
    defaultValue: initialAppointment?.all_residents,
  });

  const selectedType = useWatch({
    control,
    name: "type",
    defaultValue: initialAppointment?.type,
  });

  const recurrenceFrequency = useWatch({
    control,
    name: "recurrence_rule.frequency",
  });

  const shouldShowEndDetails =
    recurrenceFrequency?.[0] !== FrequencyEnums.NEVER;

  const transformAdditionalInfo = (
    additionalInfo?: string[],
  ): KeyValueModel[] => {
    if (!additionalInfo?.length) return [];
    let transformedAdditionalInfo: KeyValueModel[] = [];
    const selection = additionalInfo[0];

    if (selection === "booked") {
      transformedAdditionalInfo = [
        { key: "transport_required", value: "true" },
        { key: "transport_booked", value: "true" },
      ];
    } else if (selection === "not_booked") {
      transformedAdditionalInfo = [
        { key: "transport_required", value: "true" },
        { key: "transport_booked", value: "false" },
      ];
    } else if (selection === "no_transport") {
      transformedAdditionalInfo = [
        { key: "transport_required", value: "false" },
      ];
    }

    return transformedAdditionalInfo;
  };

  const submit = handleSubmit(async (data) => {
    try {
      const isRecurring =
        data.recurrence_rule?.frequency?.[0] !== FrequencyEnums.NEVER;

      const startsAt = formatDateTime(data.start_date, data.start_time);
      const endsAt = formatDateTime(data.end_date, data.end_time);

      const recurrenceRule = buildRecurrenceRule(data);

      const transformedAdditionalInfo = transformAdditionalInfo(
        data.additional_info,
      );

      const requestBody: CreateCalendarEventRequest = {
        title: data.title,
        type: data.type,
        type_specification: data.type_specification,
        location_id: data.location_id,
        location_type: data.location_type,
        location_description: data.location_description,
        status: data.status,
        notes: data.notes,
        participant_ids: data.participant_ids,
        all_residents: data.all_residents,
        role_ids: data.role_ids,
        additional_info: transformedAdditionalInfo,
        is_recurring: isRecurring,
        preparation_needed: Array.isArray(data.preparation_needed)
          ? data.preparation_needed.includes("true")
          : !!data.preparation_needed,
        starts_at: startsAt,
        ends_at: endsAt,
        recurrence_rule: recurrenceRule,
      };
      if (initialAppointment?.id) {
        await updateCalendarEvent({
          body: requestBody,
          params: { id: initialAppointment.id },
        }).unwrap();
      } else {
        await createCalendarEvent(requestBody).unwrap();
      }
      showNewToast({
        title: "Appointment created",
        message: "Successfully created appointment",
        type: "success",
      });
      if (onCreateSuccess) onCreateSuccess();
    } catch (e) {
      showErrorToast(e);
    }
  });

  return {
    control,
    handleSubmit,
    selectedType,
    shouldShowEndDetails,
    submit,
    watch,
    setValue,
    isSubmitting,
    allResidentsSelected,
    areResidentsLoading,
    userOptions,
    searchResidents,
    locationSelected,
  };
}
